import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import {
  collection, query, where, onSnapshot,
  updateDoc, doc, writeBatch,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { AppHeader } from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import { Bell, CheckCheck, Search, Trash2 } from 'lucide-react';
import { saveProjectNotificationPref } from '../utils/projectNotificationPrefs';
import '../components/pm/pm-shared.css';
import './NotificationsPage.css';
import { navigateFromNotification, notificationPreviewPath } from '../utils/userNotifications';
import { isArchitectRole, isDesignPartnerOrEngineerRole } from '../utils/userRoles';
import { filterBellsForViewer } from '../utils/portalPartnerBells';
import NotificationRow from '../components/ui/NotificationRow';
import NotificationPreviewModal from '../components/ui/NotificationPreviewModal';
import type { UserNotification } from '../utils/userNotifications';
import {
  INBOX_TYPE_GROUPS,
  groupInboxByDate,
  inboxStatusCounts,
  matchesInboxFilters,
  presentInboxTypeGroups,
  type InboxStatus,
  type InboxTypeGroup,
} from '../utils/notificationInbox';

interface FirestoreNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  leadId?: string;
  dealId?: string;
  phoneNumber?: string;
  isPriority?: boolean;
  projectType?: string;
  projectId?: string;
  taskId?: string;
  leadTodoId?: string;
  portalStep?: string;
  streamId?: string;
  sourceId?: string;
  opsTaskId?: string;
  documentId?: string;
  pdfPageNumber?: number;
  groupCommentKey?: string;
  discussion?: 'architect' | 'comments' | 'pdf';
  link?: string;
  createdAt: Date;
}

const PAGE_SIZE = 60;

const NotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<FirestoreNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<InboxStatus>('all');
  const [typeGroup, setTypeGroup] = useState<InboxTypeGroup | null>(null);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [previewNotif, setPreviewNotif] = useState<FirestoreNotification | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toUserNotification = useCallback((notif: FirestoreNotification): UserNotification => ({
    id: notif.id,
    type: notif.type,
    title: notif.title,
    message: notif.message,
    timestamp: notif.createdAt,
    read: notif.read,
    leadId: notif.leadId,
    dealId: notif.dealId,
    sourceId: notif.sourceId,
    phoneNumber: notif.phoneNumber,
    isPriority: notif.isPriority,
    projectType: notif.projectType,
    portalStep: notif.portalStep,
    projectId: notif.projectId,
    taskId: notif.taskId,
    leadTodoId: notif.leadTodoId,
    streamId: notif.streamId,
    opsTaskId: notif.opsTaskId,
    documentId: notif.documentId,
    pdfPageNumber: notif.pdfPageNumber,
    groupCommentKey: notif.groupCommentKey,
    discussion: notif.discussion,
    link: notif.link,
  }), []);

  useEffect(() => {
    if (!user?.id) return;

    const q = query(collection(db, 'userNotifications'), where('userId', '==', user.id));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs: FirestoreNotification[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          notifs.push({
            id: docSnap.id,
            userId: data.userId,
            type: data.type,
            title: data.title,
            message: data.message,
            read: data.read || false,
            leadId: data.leadId || undefined,
            dealId: data.dealId || undefined,
            phoneNumber: data.phoneNumber || undefined,
            isPriority: data.isPriority || false,
            projectType: data.projectType || undefined,
            projectId: data.projectId || undefined,
            taskId: data.taskId || undefined,
            leadTodoId: data.leadTodoId || undefined,
            portalStep: data.portalStep || undefined,
            streamId: data.streamId || undefined,
            sourceId: typeof data.sourceId === 'string' ? data.sourceId : undefined,
            opsTaskId: typeof data.opsTaskId === 'string' ? data.opsTaskId : undefined,
            documentId: typeof data.documentId === 'string' ? data.documentId : undefined,
            pdfPageNumber: typeof data.pdfPageNumber === 'number' ? data.pdfPageNumber : undefined,
            groupCommentKey: typeof data.groupCommentKey === 'string' ? data.groupCommentKey : undefined,
            discussion: data.discussion === 'architect' || data.discussion === 'comments' || data.discussion === 'pdf'
              ? data.discussion
              : undefined,
            link: typeof data.link === 'string' ? data.link : undefined,
            createdAt: data.createdAt?.toDate?.() || new Date(),
          });
        });
        notifs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setNotifications(filterBellsForViewer(notifs, user));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading notifications:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user?.id, user?.role, user?.designations, user?.orgLevel, user?.executiveBriefing]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [status, typeGroup, search]);

  const counts = useMemo(() => inboxStatusCounts(notifications), [notifications]);
  const typeChips = useMemo(() => presentInboxTypeGroups(notifications), [notifications]);

  const matched = useMemo(
    () => notifications.filter((n) => matchesInboxFilters(n, { status, typeGroup, search })),
    [notifications, status, typeGroup, search],
  );
  const visible = useMemo(() => matched.slice(0, visibleCount), [matched, visibleCount]);
  const grouped = useMemo(() => groupInboxByDate(visible), [visible]);
  const hasMore = visible.length < matched.length;


  const toggleRead = useCallback(async (notif: FirestoreNotification) => {
    try {
      await updateDoc(doc(db, 'userNotifications', notif.id), { read: !notif.read });
    } catch (err) {
      console.error('Error toggling notification read:', err);
    }
  }, []);

  const markSelectedRead = useCallback(async () => {
    const targets = notifications.filter((n) => selectedIds.has(n.id));
    if (targets.length === 0) return;
    const batch = writeBatch(db);
    for (const n of targets) batch.update(doc(db, 'userNotifications', n.id), { read: true });
    await batch.commit();
    setSelectedIds(new Set());
  }, [notifications, selectedIds]);

  const setProjectMode = useCallback(async (projectId: string | undefined, mode: 'muted' | 'mentions') => {
    if (!user?.id || !projectId) return;
    try {
      await saveProjectNotificationPref({ db, userId: user.id, projectId, mode });
    } catch (err) {
      console.error('Error saving project notification pref:', err);
      window.alert('Could not update project notification setting.');
    }
  }, [user?.id]);

  const markAllRead = useCallback(async () => {
    if (!user?.id) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    for (const n of unread) {
      batch.update(doc(db, 'userNotifications', n.id), { read: true });
    }
    await batch.commit();
  }, [user?.id, notifications]);

  const clearRead = useCallback(async () => {
    if (!user?.id) return;
    const readNotifs = notifications.filter((n) => n.read);
    if (readNotifs.length === 0) return;
    const batch = writeBatch(db);
    for (const n of readNotifs) {
      batch.delete(doc(db, 'userNotifications', n.id));
    }
    await batch.commit();
  }, [user?.id, notifications]);

  const handleClick = useCallback(
    async (notif: FirestoreNotification) => {
      if (!notif.read) {
        try {
          await updateDoc(doc(db, 'userNotifications', notif.id), { read: true });
        } catch (err) {
          console.error('Error marking notification as read:', err);
        }
      }
      await navigateFromNotification(toUserNotification(notif), {
        navigate,
        viewerRole: user?.role,
      });
    },
    [navigate, toUserNotification, user?.role],
  );

  const handleOpenFromPreview = useCallback(async () => {
    if (!previewNotif) return;
    const target = previewNotif;
    setPreviewNotif(null);
    await handleClick(target);
  }, [handleClick, previewNotif]);

  const emptyCopy =
    status === 'unread'
      ? { title: 'All caught up', sub: 'No unread notifications' }
      : { title: 'Nothing here', sub: 'Try another filter or search' };

  return (
    <>
      {isDesignPartnerOrEngineerRole(user?.role) ? (
        <ArchitectHeader
          homePath={isArchitectRole(user?.role) ? '/architect-portal' : '/designer-portal'}
          homeLinkLabel={user?.role === 'engineer' ? 'Engineer Work' : 'Design Work'}
          showStandup={user?.role !== 'engineer'}
        />
      ) : (
        <AppHeader />
      )}
      <div className="notif-inbox">
        <div className="notif-inbox-toolbar">
          <div className="notif-inbox-toolbar-left">
            <h1 className="notif-inbox-title">Notifications</h1>
            <div className="notif-inbox-stats">
              <span className={`notif-stat${counts.unread ? ' notif-stat-unread' : ''}`}>
                {counts.unread} unread
              </span>
              <span className="notif-stat">{counts.all} total</span>
            </div>
          </div>
          <div className="notif-inbox-toolbar-right">
            {selectedIds.size > 0 && (
              <button type="button" className="notif-inbox-action" onClick={() => void markSelectedRead()}>
                <CheckCheck size={13} /> Mark selected read ({selectedIds.size})
              </button>
            )}
            {counts.unread > 0 && (
              <button type="button" className="notif-inbox-action" onClick={markAllRead}>
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
            {counts.read > 0 && (
              <button type="button" className="notif-inbox-action" onClick={clearRead}>
                <Trash2 size={13} /> Clear read
              </button>
            )}
          </div>
        </div>

        <div className="notif-inbox-filters">
          <div className="notif-inbox-views" role="tablist" aria-label="Read status">
            {(['all', 'unread', 'read'] as InboxStatus[]).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={status === key}
                className={`notif-view-btn${status === key ? ' active' : ''}`}
                onClick={() => setStatus(key)}
              >
                {key === 'unread' ? 'Unread' : key === 'all' ? 'All' : 'Read'}
                <span className="notif-view-count">{counts[key]}</span>
              </button>
            ))}
          </div>

          <label className="notif-inbox-search">
            <Search size={13} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search notifications"
            />
          </label>
        </div>

        {typeChips.length > 0 && (
          <div className="notif-inbox-chips" role="group" aria-label="Notification type">
            {INBOX_TYPE_GROUPS.filter((g) => typeChips.includes(g.id)).map((g) => (
              <button
                key={g.id}
                type="button"
                className={`notif-chip notif-chip-${g.id}${typeGroup === g.id ? ' active' : ''}`}
                onClick={() => setTypeGroup((prev) => (prev === g.id ? null : g.id))}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}

        <div className="notif-inbox-list">
          {loading ? (
            <div className="notif-inbox-empty">
              <div className="notif-inbox-loading-bar" />
            </div>
          ) : matched.length === 0 ? (
            <div className="notif-inbox-empty">
              <Bell size={22} />
              <p>{emptyCopy.title}</p>
              <span>{emptyCopy.sub}</span>
            </div>
          ) : (
            <>
              {grouped.map((group) => (
                <section key={group.label} className="notif-inbox-group">
                  <div className="notif-inbox-group-header">{group.label}</div>
                  {group.items.map((notif) => {
                    const previewPath = notificationPreviewPath(toUserNotification(notif), user?.role);
                    return (
                    <NotificationRow
                      key={notif.id}
                      notification={{
                        id: notif.id,
                        type: notif.type,
                        title: notif.title,
                        message: notif.message,
                        timestamp: notif.createdAt,
                        read: notif.read,
                        isPriority: notif.isPriority,
                        projectId: notif.projectId,
                      }}
                      onClick={() => handleClick(notif)}
                      showPreview={!!previewPath}
                      onPreview={() => setPreviewNotif(notif)}
                      showSelect
                      selected={selectedIds.has(notif.id)}
                      onToggleSelect={() => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(notif.id)) next.delete(notif.id);
                          else next.add(notif.id);
                          return next;
                        });
                      }}
                      onToggleRead={() => void toggleRead(notif)}
                      onMuteProject={
                        notif.projectId
                          ? () => void setProjectMode(notif.projectId, 'muted')
                          : undefined
                      }
                      onMentionsOnly={
                        notif.projectId
                          ? () => void setProjectMode(notif.projectId, 'mentions')
                          : undefined
                      }
                    />
                    );
                  })}
                </section>
              ))}
              {hasMore && (
                <div className="notif-inbox-more">
                  <button type="button" className="notif-inbox-action" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {previewNotif ? (
        <NotificationPreviewModal
          notification={toUserNotification(previewNotif)}
          viewerRole={user?.role}
          onClose={() => setPreviewNotif(null)}
          onOpenFullPage={() => void handleOpenFromPreview()}
        />
      ) : null}
    </>
  );
};

export default NotificationsPage;
